import React, { useState } from "react";
import { Tooltip, IconButton } from "@material-ui/core";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import CachedIcon from "@material-ui/icons/Cached";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import InfoIcon from "@material-ui/icons/Info";
import EcoIcon from "@material-ui/icons/Eco";
import Transaction from "../../../classes/Transaction/Transaction";
import FullScreenDialog from "../../Dialog/FullScreenDialog";
import NftTable from "../NftTable/NftTable";
import {
  useStyles,
  CarbonCreditsWidgetLogic,
} from "./CarbonCreditsWidget.module";

const CarbonCreditsWidget = ({
  user,
  operations,
  goToProjects,
  nfts,
  sbts,
  nftTooltip,
  sbtTooltip,
}) => {
  const classes = useStyles();
  const { translator } = CarbonCreditsWidgetLogic();

  const [openNftDialog, setOpenNftDialog] = useState(false);
  const [openSbtDialog, setOpenSbtDialog] = useState(false);

  const nftIsSbt =
    Array.isArray(nfts) && nfts.length > 0 ? nfts[0].is_sbt : false;
  const sbtIsSbt =
    Array.isArray(sbts) && sbts.length > 0 ? sbts[0].is_sbt : true;

  const available = user.availableNFT || 0;
  const converted = user.availableSBT || 0;
  const total = available + converted;
  const usagePercent = total > 0 ? Math.round((converted / total) * 100) : 0;
  const availablePercent = total > 0 ? (available / total) * 100 : 0;
  const convertedPercent = total > 0 ? (converted / total) * 100 : 0;

  const recentActivity = (operations || [])
    .filter(
      (op) => op.type === Transaction.BUY_NFT || op.type === Transaction.BUY_SBT
    )
    .slice(0, 5)
    .map((op) => {
      const isPurchase = op.type === Transaction.BUY_NFT;
      return {
        type: isPurchase
          ? translator("carbonCreditsWidget.transaction")
          : translator("carbonCreditsWidget.converted"),
        date: op.formattedDate,
        amount: isPurchase ? `+${op.total_coins}` : `-${op.total_coins}`,
        isPurchase,
      };
    });

  return (
    <div className={classes.container}>
      {openNftDialog && (
        <FullScreenDialog
          openFromParent={openNftDialog}
          setOpenFromParent={setOpenNftDialog}
          children={
            <NftTable
              title={translator("carbonCreditsWidget.available")}
              nfts={nfts}
              user={user._id}
              is_sbt={nftIsSbt}
            />
          }
        />
      )}

      {openSbtDialog && (
        <FullScreenDialog
          openFromParent={openSbtDialog}
          setOpenFromParent={setOpenSbtDialog}
          children={
            <NftTable
              title={translator("carbonCreditsWidget.convertedUsed")}
              nfts={sbts}
              user={user._id}
              is_sbt={sbtIsSbt}
            />
          }
        />
      )}

      <div className={classes.header}>
        <h2 className={classes.title}>
          {translator("carbonCreditsWidget.yourCarbonCredits")}
        </h2>
        <span className={classes.activeBadge}>
          <EcoIcon style={{ fontSize: 15 }} />
          {translator("carbonCreditsWidget.active")}
        </span>
      </div>

      <div className={classes.summaryCard}>
        <div>
          <div className={classes.summaryLabel}>
            {translator("carbonCreditsWidget.total")}
          </div>
          <div className={classes.summaryValue}>
            {total} {translator("carbonCreditsWidget.credits")}
          </div>
        </div>
        <div className={classes.summaryRight}>
          <div className={classes.summaryLabel}>
            {translator("carbonCreditsWidget.retired")}
          </div>
          <div className={classes.summaryValue}>
            {usagePercent}% {translator("carbonCreditsWidget.used")}
          </div>
        </div>
      </div>

      <div className={classes.cardsRow}>
        <div className={classes.card}>
          <div className={classes.cardTopRow}>
            <div className={classes.cardHeader}>
              <AccessTimeIcon style={{ fontSize: 16 }} />
              {translator("carbonCreditsWidget.available")}
            </div>
            {nftTooltip && (
              <Tooltip
                title={
                  <span className={classes.tooltipContent}>{nftTooltip}</span>
                }
                placement="bottom-start"
                arrow
              >
                <IconButton
                  className={classes.infoBtn}
                  onClick={() => setOpenNftDialog(true)}
                >
                  <InfoIcon style={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <div className={classes.bigNumberGreen}>{available}</div>
          <div className={classes.progressRow}>
            <span>0</span>
            <div className={classes.progressTrack}>
              <div
                className={classes.progressFillGreen}
                style={{ width: `${availablePercent}%` }}
              />
            </div>
            <span>{total}</span>
          </div>
          <div
            className={classes.tapDetails}
            onClick={() => setOpenNftDialog(true)}
          >
            <InfoOutlinedIcon style={{ fontSize: 14 }} />
            {translator("carbonCreditsWidget.tapForDetails")}
          </div>
          <button className={classes.buyMoreBtn} onClick={goToProjects}>
            + {translator("carbonCreditsWidget.getMore")} ↗
          </button>
        </div>

        <div className={classes.card}>
          <div className={classes.cardTopRow}>
            <div className={classes.cardHeader}>
              <CachedIcon style={{ fontSize: 16 }} />
              {translator("carbonCreditsWidget.convertedUsed")}
            </div>
            {sbtTooltip && (
              <Tooltip
                title={
                  <span className={classes.tooltipContent}>{sbtTooltip}</span>
                }
                placement="bottom-start"
                arrow
              >
                <IconButton
                  className={classes.infoBtn}
                  onClick={() => setOpenSbtDialog(true)}
                >
                  <InfoIcon style={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <div className={classes.bigNumberWhite}>{converted}</div>
          <div className={classes.progressRow}>
            <span>0</span>
            <div className={classes.progressTrack}>
              <div
                className={classes.progressFillWhite}
                style={{ width: `${convertedPercent}%` }}
              />
            </div>
            <span>{total}</span>
          </div>
          <div
            className={classes.tapDetails}
            onClick={() => setOpenSbtDialog(true)}
          >
            <InfoOutlinedIcon style={{ fontSize: 14 }} />
            {translator("carbonCreditsWidget.tapForDetails")}
          </div>
        </div>
      </div>

      {user._id && total > 0 && recentActivity.length > 0 && (
        <div className={classes.activityCard}>
          <div className={classes.activityTitle}>
            {translator("carbonCreditsWidget.recentActivity")}
          </div>
          {recentActivity.map((item, i) => (
            <div key={i} className={classes.activityItem}>
              <div className={classes.activityLeft}>
                <span
                  className={
                    item.isPurchase ? classes.dotGreen : classes.dotWhite
                  }
                />
                <div>
                  <div className={classes.activityType}>{item.type}</div>
                  <div className={classes.activityDate}>{item.date}</div>
                </div>
              </div>
              <div
                className={
                  item.isPurchase ? classes.amountGreen : classes.amountWhite
                }
              >
                {item.amount}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CarbonCreditsWidget;
